
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { FlaskRound } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface Material {
  id: string;
  name: string;
  type: string;
  percentage?: number;
}

interface FormulaCompositionStepProps {
  formData: {
    materials: Material[];
  };
  updateFormData: (data: { materials: Material[] }) => void;
}

export const FormulaCompositionStep = ({ formData, updateFormData }: FormulaCompositionStepProps) => {
  const updatePercentage = (id: string, value: string) => {
    const percentage = parseFloat(value);
    if (isNaN(percentage)) return;
    
    const updatedMaterials = formData.materials.map(material => {
      if (material.id === id) {
        return { ...material, percentage };
      }
      return material;
    });
    
    updateFormData({ materials: updatedMaterials });
  };
  
  const totalPercentage = formData.materials.reduce((sum, material) => {
    return sum + (material.percentage || 0);
  }, 0);
  
  const getMaterialsWithType = (type: string) => {
    return formData.materials.filter(m => m.type === type);
  };

  const rawMaterials = getMaterialsWithType('raw');
  const chemicals = getMaterialsWithType('chemical');

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Define Composition</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Specify the percentage of each material in your formula. The total should equal 100%.
        </p>
      </div>
      
      {formData.materials.length === 0 ? (
        <div className="text-center p-8 border rounded-md bg-muted/30">
          <FlaskRound className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">No materials to define composition for.</p>
          <p className="text-xs text-muted-foreground mt-1">Go back to the Materials step to add ingredients first.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {rawMaterials.length > 0 && (
            <div>
              <h4 className="text-base font-medium mb-3">Raw Materials</h4>
              <Card>
                <CardContent className="pt-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Material Name</TableHead>
                        <TableHead className="text-right w-[150px]">Percentage (%)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rawMaterials.map((material) => (
                        <TableRow key={material.id}>
                          <TableCell className="font-medium">{material.name}</TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={material.percentage || ''}
                              onChange={(e) => updatePercentage(material.id, e.target.value)}
                              className="w-24 ml-auto"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
          
          {chemicals.length > 0 && (
            <div>
              <h4 className="text-base font-medium mb-3">Chemicals</h4>
              <Card>
                <CardContent className="pt-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Chemical Name</TableHead>
                        <TableHead className="text-right w-[150px]">Percentage (%)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {chemicals.map((material) => (
                        <TableRow key={material.id}>
                          <TableCell className="font-medium">{material.name}</TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={material.percentage || ''}
                              onChange={(e) => updatePercentage(material.id, e.target.value)}
                              className="w-24 ml-auto"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
          
          <div className="pt-4 border-t flex justify-between items-center">
            <span>Total Composition:</span>
            <Badge 
              variant={Math.abs(totalPercentage - 100) < 0.01 ? "secondary" : "outline"}
              className={`ml-2 text-base px-3 py-1 ${
                Math.abs(totalPercentage - 100) < 0.01 
                  ? 'bg-green-100 text-green-800 hover:bg-green-100' 
                  : 'border-amber-500 text-amber-700'
              }`}
            >
              {totalPercentage.toFixed(2)}%
            </Badge>
          </div>
          
          {Math.abs(totalPercentage - 100) >= 0.01 && (
            <p className="text-amber-500 text-sm">
              {totalPercentage < 100 
                ? `Missing ${(100 - totalPercentage).toFixed(2)}% to reach 100%` 
                : `Exceeding 100% by ${(totalPercentage - 100).toFixed(2)}%`}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
